// Chat mode's pages beside the channel (M2-06, CHAT-UX): the landing, which opens a channel and
// never a page of its own (the last one visited, else #general; an empty company gets one
// compact empty state), Browse channels (every channel the person may see, with Join / Leave),
// Saved messages, Threads (CHAT-CONTROLS; Slack's view: the threads the person started, replied
// in or follows, with unread state) and Archived channels (with Restore for the channel's
// admins), and the dialogs the sidebar opens: a new channel or agent room, a new direct
// message. No eyebrow, hero heading or explanatory paragraph: chat screens are a thin bar and
// their rows.
import {
  AvatarStack,
  AvatarTile,
  Badge,
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  EmptyState,
  Field,
  FieldLabel,
  GroupHeader,
  Input,
  ListRow,
  NativeSelect,
  NativeSelectOption,
  relativeTime,
  SearchField,
  Switch,
  Textarea,
  toast,
  WorkerBadge,
  workerName,
} from "@companyos/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import {
  ArchiveRestoreIcon,
  BellIcon,
  BellOffIcon,
  BookmarkIcon,
  BotIcon,
  CheckCheckIcon,
  CheckIcon,
  HashIcon,
  LockIcon,
  MessageCircleIcon,
  MessagesSquareIcon,
  PlusIcon,
  SquareKanbanIcon,
} from "lucide-react";
import { type FormEvent, type ReactNode, useEffect, useState } from "react";
import { keepFocus } from "../../focus/focus";
import { isManager, isOperatorOnly } from "../company";
import { OperatorOnlyDisabled, OperatorOnlyNotice } from "../operator-only";
import { myCrewQuery } from "../queries";
import { LoadError, Loading } from "../screen-parts";
import { type ShareInput, SpaceSharesField } from "../work/space-shares";
import { ParticipantPicker } from "./agent-room";
import {
  authorOf,
  type ChannelGrouping,
  channelLabel,
  dmCandidates,
  isAgentRoom,
  isDm,
  joinedOf,
  landingChannel,
  listedInChat,
  readLastChannel,
  roomCandidates,
  shownUnread,
  sidebarSections,
} from "./chat-model";
import {
  bookmarksQuery,
  type ChannelPrefs,
  channelSharesQuery,
  channelsQuery,
  chatKeys,
  membersQuery,
  myThreadsQuery,
  prefsQuery,
  type SidebarChannel,
  spacesQuery,
  type ThreadSummaryView,
} from "./chat-queries";
import { RemindersSection } from "./chat-switch";
import { ScheduledSection } from "./chat-switch-b";
import { useOpenDm } from "./member-card";
import { useThreadReadActions } from "./thread-reads";
import { type ChatContext, useChat } from "./use-chat";

/** Icon of a channel row by kind. */
export function channelIcon(c: Pick<SidebarChannel, "kind" | "is_private" | "is_agent_room">) {
  if (isDm(c)) return MessageCircleIcon;
  if (isAgentRoom(c)) return BotIcon;
  if (c.kind === "board_home") return SquareKanbanIcon;
  if (c.is_private) return LockIcon;
  return HashIcon;
}

/** A channel's link as a ListRow title: the row's own props (title, class) pass through. */
function ChannelLink({
  chat,
  channel,
  ...props
}: {
  chat: ChatContext;
  channel: SidebarChannel;
  className?: string;
  children?: ReactNode;
  "aria-label"?: string;
}) {
  return (
    <Link
      {...props}
      to="/c/$slug/chat/$channelId"
      params={{ slug: chat.company.slug, channelId: channel.id }}
      search={keepFocus as never}
    />
  );
}

/**
 * The chat data every page and the sidebar share (three calls, all cached). Agency operators
 * read without a membership: every channel they see counts as joined for them.
 */
export function useChatLists(chat: ChatContext, grouping: ChannelGrouping = "auto") {
  const spaces = useQuery(spacesQuery(chat.api, chat.co));
  const channels = useQuery(channelsQuery(chat.api, chat.co));
  const prefs = useQuery({ ...prefsQuery(chat.api, chat.co), retry: false });
  // MULTI-SPACE-ACCESS: a shared channel also sits under the spaces it is shared into.
  const shares = useQuery({ ...channelSharesQuery(chat.api, chat.co), retry: false });
  const byChannel = new Map<string, ChannelPrefs>(
    (prefs.data?.data ?? []).map((p) => [p.channel_id, p]),
  );
  // The channel query carries archived channels (for the Archived channels page); the lists
  // that the sidebar and Browse read are the live ones.
  const list = (channels.data?.data ?? []).filter((c) => !c.archived_at);
  const joined = joinedOf(
    list,
    chat.meId !== null && prefs.isSuccess ? (prefs.data?.data ?? []) : undefined,
  );
  const sections = sidebarSections(spaces.data?.data ?? [], list, joined, byChannel, {
    agents: chat.agents,
    grouping,
    shares: shares.data?.data ?? [],
  });
  // Settled: the channels are in and the person's settings have answered (either way).
  const ready = channels.isSuccess && (chat.meId === null || !prefs.isPending);
  return { spaces, channels, prefs: byChannel, joined, sections, list, ready };
}

/** The thin bar at the top of a chat page: a small h1 and its actions. */
export function ChatPageBar({
  title,
  children,
  actions,
}: {
  title: string;
  children?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <div
      data-slot="chat-page-bar"
      className="flex h-11 shrink-0 items-center gap-3 border-b border-border-subtle pr-3 pl-5 max-md:pl-3"
    >
      <h1 className="min-w-0 shrink-0 truncate text-[15px] font-semibold text-foreground">
        {title}
      </h1>
      {children}
      {actions ? <div className="ml-auto flex items-center gap-1.5">{actions}</div> : null}
    </div>
  );
}

/**
 * `/chat`: opens a channel (CHAT-UX: the last one visited here, else #general, else the first
 * listed) and replaces itself in history. Only a company with no channel at all stays: one
 * compact empty state with the way to make the first channel.
 */
export function ChatLanding() {
  const chat = useChat();
  const lists = useChatLists(chat);
  const navigate = useNavigate();
  const [creating, setCreating] = useState(false);
  const target = lists.ready ? landingChannel(lists.sections, readLastChannel(chat.co)) : null;
  const targetId = target?.id;
  const slug = chat.company.slug;
  useEffect(() => {
    if (!targetId) return;
    void navigate({
      to: "/c/$slug/chat/$channelId",
      params: { slug, channelId: targetId },
      search: keepFocus as never,
      replace: true,
    });
  }, [navigate, slug, targetId]);
  if (lists.channels.isError)
    return (
      <div className="p-6">
        <LoadError what="Channels" error={lists.channels.error} />
      </div>
    );
  if (!lists.ready || targetId)
    return (
      <div
        role="status"
        aria-label="Loading channels"
        data-page="chat-landing"
        className="p-6 text-sm text-muted-foreground"
      >
        Opening chat…
      </div>
    );
  const canWrite = chat.meId !== null && !chat.readOnly;
  // OPERATOR-ONLY-UX: the agency's operator with no person membership cannot open a channel.
  const operatorOnly = isOperatorOnly(chat.company);
  return (
    <div data-page="chat-empty" className="flex h-full items-center justify-center p-6">
      <EmptyState
        variant="card"
        className="max-w-sm"
        icon={HashIcon}
        titleAs="h1"
        title="No channels yet"
        description={
          operatorOnly ? (
            <OperatorOnlyNotice slug={chat.company.slug} compact className="[&_a]:underline" />
          ) : (
            "Everyone in the company joins the first channel."
          )
        }
        actions={
          canWrite ? (
            <Button onClick={() => setCreating(true)}>
              <PlusIcon data-icon="inline-start" />
              Create a channel
            </Button>
          ) : operatorOnly ? (
            <OperatorOnlyDisabled>
              <Button>
                <PlusIcon data-icon="inline-start" />
                Create a channel
              </Button>
            </OperatorOnlyDisabled>
          ) : null
        }
      />
      {creating ? <NewChannelDialog chat={chat} onClose={() => setCreating(false)} /> : null}
    </div>
  );
}

function ChannelGlyph({ channel }: { channel: SidebarChannel }) {
  const Icon = channelIcon(channel);
  return <Icon aria-hidden="true" className="size-4 text-faint" />;
}

/**
 * A row beside its `ChannelGlyph`: the glyph already says "channel", so the visible title is the
 * bare name ("general", not "# #general"); a DM keeps its people's names. The link's accessible
 * name keeps the full label ("#general").
 */
function glyphRow(c: SidebarChannel, chat: ChatContext) {
  const label = channelLabel(c, chat);
  return {
    title: isDm(c) ? label : c.name,
    render: <ChannelLink chat={chat} channel={c} aria-label={label} />,
  };
}

export function BrowseChannels() {
  const chat = useChat();
  const lists = useChatLists(chat);
  const queryClient = useQueryClient();
  const [q, setQ] = useState("");
  const [creating, setCreating] = useState(false);
  const join = useMutation({
    mutationFn: ({ id, joined }: { id: string; joined: boolean }) =>
      chat.api.client.channels.setPrefs({ params: { co: chat.co, id }, body: { joined } }),
    onSuccess: (p) => {
      void queryClient.invalidateQueries({ queryKey: chatKeys.prefs(chat.co) });
      void queryClient.invalidateQueries({ queryKey: chatKeys.channels(chat.co) });
      const c = lists.list.find((x) => x.id === p.channel_id);
      toast.add({
        title: `${p.joined ? "Joined" : "Left"} ${c ? channelLabel(c, chat) : "the channel"}`,
        type: "success",
      });
    },
  });
  const spaces = new Map((lists.spaces.data?.data ?? []).map((s) => [s.id, s.name]));
  const needle = q.trim().toLowerCase();
  const rows = lists.list
    .filter((c) => !isDm(c) && !c.archived_at && listedInChat(c))
    .filter((c) => !needle || `${c.name} ${c.description ?? ""}`.toLowerCase().includes(needle))
    .sort(
      (a, b) =>
        (spaces.get(a.space_id ?? "") ?? "").localeCompare(spaces.get(b.space_id ?? "") ?? "") ||
        a.name.localeCompare(b.name),
    );
  const canWrite = chat.meId !== null && !chat.readOnly;
  // OPERATOR-ONLY-UX: New channel stays visible, unavailable; the page says why once.
  const operatorOnly = !chat.readOnly && isOperatorOnly(chat.company);
  return (
    <div data-page="chat-browse" className="flex h-full min-h-0 flex-col">
      <ChatPageBar
        title="Browse channels"
        actions={
          canWrite ? (
            <Button size="sm" onClick={() => setCreating(true)}>
              <PlusIcon data-icon="inline-start" />
              New channel
            </Button>
          ) : operatorOnly ? (
            <OperatorOnlyDisabled>
              <Button size="sm">
                <PlusIcon data-icon="inline-start" />
                New channel
              </Button>
            </OperatorOnlyDisabled>
          ) : null
        }
      >
        <SearchField
          aria-label="Search channels"
          placeholder="Search channels"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="h-8 w-56 max-md:w-36"
        />
      </ChatPageBar>
      <div className="min-h-0 flex-1 overflow-y-auto px-3 py-2 max-md:px-1">
        {operatorOnly ? (
          <OperatorOnlyNotice slug={chat.company.slug} className="mb-2 max-w-2xl" />
        ) : null}
        {lists.channels.isError ? <LoadError what="Channels" error={lists.channels.error} /> : null}
        {lists.channels.isPending ? <Loading label="Loading channels" /> : null}
        {lists.channels.isSuccess && !rows.length ? (
          <EmptyState
            variant="compact"
            icon={HashIcon}
            title="No channels match"
            description={
              canWrite ? "Try another word, or create the channel." : "Try another word."
            }
          />
        ) : null}
        <div className="flex flex-col">
          {rows.map((c) => {
            const joined = lists.joined.has(c.id);
            return (
              <ListRow
                key={c.id}
                data-channel={c.name}
                lead={<ChannelGlyph channel={c} />}
                {...glyphRow(c, chat)}
                chips={
                  <>
                    <Badge variant="outline">{spaces.get(c.space_id ?? "") ?? "No space"}</Badge>
                    {c.is_default ? <Badge variant="neutral">Default</Badge> : null}
                    {c.is_private ? <Badge variant="neutral">Private</Badge> : null}
                    {isAgentRoom(c) ? <Badge variant="accent">Agent room</Badge> : null}
                    {joined ? <Badge variant="success">Joined</Badge> : null}
                  </>
                }
                meta={c.description ?? undefined}
                trailing={
                  canWrite ? (
                    <Button
                      size="sm"
                      variant={joined ? "ghost" : "secondary"}
                      className="relative z-10"
                      disabled={join.isPending}
                      aria-label={`${joined ? "Leave" : "Join"} ${channelLabel(c, chat)}`}
                      onClick={() => join.mutate({ id: c.id, joined: !joined })}
                    >
                      {joined ? "Leave" : "Join"}
                    </Button>
                  ) : undefined
                }
              />
            );
          })}
        </div>
      </div>
      {creating ? <NewChannelDialog chat={chat} onClose={() => setCreating(false)} /> : null}
    </div>
  );
}

/**
 * A new channel, or (with the agents add-on) a new agent room: `agentRooms.create` makes the
 * channel and its turn-taking at once, with the DEs, the caller's own crew and people picked.
 * A company without a space yet gets its General space with the channel.
 */
export function NewChannelDialog({
  chat,
  room = false,
  onClose,
}: {
  chat: ChatContext;
  room?: boolean;
  onClose: () => void;
}) {
  const spaces = useQuery(spacesQuery(chat.api, chat.co));
  const list = (spaces.data?.data ?? []).filter((s) => !s.archived_at);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [spaceId, setSpaceId] = useState("");
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [isPrivate, setPrivate] = useState(false);
  const [agentRoom, setAgentRoom] = useState(room);
  const [picked, setPicked] = useState<string[]>([]);
  // MULTI-SPACE-ACCESS: "Also share with" (never for a private channel: its members are its grant).
  const [shares, setShares] = useState<ShareInput[]>([]);
  const withTurns = agentRoom && chat.agents;
  const crew = useQuery({ ...myCrewQuery(chat.api, chat.co), enabled: withTurns, retry: false });
  const candidates = roomCandidates(
    null,
    chat.members,
    chat.meId,
    crew.data ? { id: crew.data.crew.membership_id, name: crew.data.agent.display_name } : null,
  );
  const space = spaceId || list[0]?.id || "";
  const noSpace = spaces.isSuccess && list.length === 0;
  // An agent room is made by `agentRooms.create` (no shares there): its Spaces tab shares it.
  const sharing = isPrivate || withTurns ? [] : shares.filter((s) => s.space_id !== space);
  const save = useMutation({
    mutationFn: async () => {
      let spaceFor = space;
      if (!spaceFor) {
        const made = await chat.api.client.spaces.create({
          params: { co: chat.co },
          body: { name: "General", kind: "project" },
        });
        spaceFor = made.id;
      }
      if (withTurns) {
        const r = await chat.api.client.agentRooms.create({
          params: { co: chat.co },
          body: {
            space_id: spaceFor,
            name: name.trim(),
            is_private: isPrivate,
            ...(picked.length ? { participant_ids: picked } : {}),
          },
        });
        queryClient.setQueryData(chatKeys.room(chat.co, r.room.channel_id), r);
        return { id: r.room.channel_id, kind: "agent_room" as const, name: r.name };
      }
      return chat.api.client.channels.create({
        params: { co: chat.co },
        body: {
          space_id: spaceFor,
          name: name.trim(),
          is_private: isPrivate,
          ...(agentRoom ? { kind: "agent_room" as const } : {}),
          ...(description.trim() ? { description: description.trim() } : {}),
          ...(sharing.length ? { shares: sharing } : {}),
        },
      });
    },
    onSuccess: async (c) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: chatKeys.spaces(chat.co) }),
        queryClient.invalidateQueries({ queryKey: chatKeys.channels(chat.co) }),
        queryClient.invalidateQueries({ queryKey: chatKeys.prefs(chat.co) }),
      ]);
      toast.add({ title: `Created ${channelLabel(c, chat)}`, type: "success" });
      onClose();
      void navigate({
        to: "/c/$slug/chat/$channelId",
        params: { slug: chat.company.slug, channelId: c.id },
        search: keepFocus as never,
      });
    },
  });
  const ready = name.trim() !== "" && (space !== "" || noSpace);
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (ready) save.mutate();
  };
  return (
    <Dialog open onOpenChange={(o) => !o && !save.isPending && onClose()}>
      <DialogContent>
        <form onSubmit={submit} className="flex flex-col gap-5">
          <DialogHeader>
            <DialogTitle>{withTurns ? "New agent room" : "New channel"}</DialogTitle>
            <DialogDescription>
              {withTurns
                ? "Agents here take turns in the order they join; people post any time and can take over."
                : "Channels live in a space; topics keep each conversation apart."}
            </DialogDescription>
          </DialogHeader>
          {noSpace ? (
            <p className="m-0 text-sm text-muted-foreground">
              The company has no space yet: this channel starts its General space.
            </p>
          ) : (
            <Field>
              <FieldLabel htmlFor="channel-space">Space</FieldLabel>
              <NativeSelect
                id="channel-space"
                value={space}
                onChange={(e) => setSpaceId(e.target.value)}
              >
                {list.map((s) => (
                  <NativeSelectOption key={s.id} value={s.id}>
                    {s.name}
                  </NativeSelectOption>
                ))}
              </NativeSelect>
            </Field>
          )}
          {noSpace || isPrivate || withTurns ? null : (
            <SpaceSharesField
              spaces={list}
              homeSpaceId={space}
              value={sharing}
              onChange={setShares}
              what="channel"
            />
          )}
          <Field>
            <FieldLabel htmlFor="channel-name">Name</FieldLabel>
            <Input
              id="channel-name"
              className="h-10"
              placeholder="e.g. offers"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </Field>
          {withTurns ? null : (
            <Field>
              <FieldLabel htmlFor="channel-description">Description</FieldLabel>
              <Textarea
                id="channel-description"
                rows={2}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </Field>
          )}
          <Field orientation="horizontal">
            <Switch
              id="channel-private"
              checked={isPrivate}
              onCheckedChange={(v) => setPrivate(Boolean(v))}
            />
            <FieldLabel htmlFor="channel-private">Private: members only</FieldLabel>
          </Field>
          <Field orientation="horizontal">
            <Switch
              id="channel-room"
              checked={agentRoom}
              onCheckedChange={(v) => setAgentRoom(Boolean(v))}
            />
            <FieldLabel htmlFor="channel-room">
              Agent room: agents work here; people can take over
            </FieldLabel>
          </Field>
          {withTurns ? (
            <Field>
              <FieldLabel>Participants</FieldLabel>
              <ParticipantPicker
                idPrefix="new-room"
                candidates={candidates}
                value={picked}
                onChange={setPicked}
              />
            </Field>
          ) : null}
          <DialogFooter>
            <DialogClose render={<Button variant="ghost" type="button" />}>Cancel</DialogClose>
            <Button type="submit" disabled={!ready || save.isPending}>
              {withTurns ? "Create room" : "Create channel"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/**
 * A new direct message (CHAT-DM): every member of the company, people and DEs, with search — no
 * space or channel in common needed. Pick one (or more for a group); the DM opens, or reopens
 * when it exists.
 */
export function NewMessageDialog({ chat, onClose }: { chat: ChatContext; onClose: () => void }) {
  const [picked, setPicked] = useState<string[]>([]);
  const [q, setQ] = useState("");
  const candidates = dmCandidates(chat.members, chat.meId, q);
  const open = useOpenDm(chat, onClose);
  const toggle = (id: string) =>
    setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (picked.length) open.mutate(picked);
  };
  const failed = chat.peopleDirectory.failed || chat.deDirectory.failed;
  const loading = !failed && (!chat.peopleDirectory.complete || !chat.deDirectory.complete);
  return (
    <Dialog open onOpenChange={(o) => !o && !open.isPending && onClose()}>
      <DialogContent>
        <form onSubmit={submit} className="flex flex-col gap-4">
          <DialogHeader>
            <DialogTitle>New message</DialogTitle>
          </DialogHeader>
          <SearchField
            aria-label="Search people and DEs"
            placeholder="Search people and DEs"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="w-full"
            autoFocus
          />
          <ul
            aria-label="Members"
            data-slot="dm-picker"
            className="m-0 flex max-h-72 list-none flex-col overflow-y-auto p-0"
          >
            {candidates.map((m) => {
              const de = m.kind === "de";
              const name = de ? workerName(m.name) : m.name;
              const on = picked.includes(m.id);
              return (
                <li key={m.id}>
                  <button
                    type="button"
                    aria-pressed={on}
                    data-member={m.id}
                    onClick={() => toggle(m.id)}
                    className="flex w-full cursor-pointer items-center gap-2.5 rounded-md border-0 bg-transparent px-2 py-1.5 text-left text-sm hover:bg-sunken focus-visible:outline-2 focus-visible:outline-ring aria-pressed:bg-primary-soft"
                  >
                    <AvatarTile name={name} kind={de ? "agent" : "person"} size="xs" />
                    <span className="min-w-0 truncate">{name}</span>
                    {de ? <WorkerBadge kind="de" size="sm" /> : null}
                    <span className="min-w-0 flex-1 truncate text-xs text-muted-foreground">
                      {de ? (m.seat?.replace(/\s*\(DE\)$/, "") ?? "") : (m.role ?? "")}
                    </span>
                    {on ? <CheckIcon aria-hidden="true" className="size-4 text-primary" /> : null}
                  </button>
                </li>
              );
            })}
          </ul>
          {failed ? (
            // A directory page failed: the list may miss people; say so, with a retry.
            <p role="alert" className="m-0 flex items-center gap-2 text-sm text-danger-text">
              Some members could not be loaded.
              <Button
                type="button"
                variant="link"
                size="xs"
                className="h-5 px-0"
                onClick={() => {
                  if (chat.peopleDirectory.failed) chat.peopleDirectory.retry();
                  if (chat.deDirectory.failed) chat.deDirectory.retry();
                }}
              >
                Retry
              </Button>
            </p>
          ) : !candidates.length ? (
            <p className="m-0 text-sm text-muted-foreground">
              {loading
                ? "Loading members…"
                : q.trim()
                  ? "No one matches."
                  : "No other members yet."}
            </p>
          ) : loading ? (
            <p className="m-0 text-xs text-muted-foreground">Loading more members…</p>
          ) : null}
          <DialogFooter>
            <DialogClose render={<Button variant="ghost" type="button" />}>Cancel</DialogClose>
            <Button type="submit" disabled={!picked.length || open.isPending}>
              {picked.length > 1 ? `Message ${picked.length}` : "Message"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/**
 * `/chat/unreads` (CHAT-UX; Slack's Unreads view): every joined conversation with unread
 * messages, newest activity first, each a row to its channel. The sidebar's single Unreads row
 * opens it, so the sidebar stays short however many channels are unread.
 */
export function UnreadsPage() {
  const chat = useChat();
  const lists = useChatLists(chat);
  const rows = lists.sections.unreads;
  return (
    <div data-page="chat-unreads" className="flex h-full min-h-0 flex-col">
      <ChatPageBar title="Unreads">
        {rows.length ? (
          <span className="text-xs text-muted-foreground tabular-nums">
            {rows.length} {rows.length === 1 ? "conversation" : "conversations"}
          </span>
        ) : null}
      </ChatPageBar>
      <div className="min-h-0 flex-1 overflow-y-auto px-3 py-2 max-md:px-1">
        {lists.channels.isError ? <LoadError what="Channels" error={lists.channels.error} /> : null}
        {!lists.ready && !lists.channels.isError ? <Loading label="Loading unreads" /> : null}
        {lists.ready && !rows.length ? (
          <EmptyState
            variant="compact"
            icon={MessageCircleIcon}
            title="All caught up"
            description="Nothing unread in the channels and messages you are in."
          />
        ) : null}
        <div className="flex flex-col">
          {rows.map((c) => {
            const unread = shownUnread(c, lists.prefs.get(c.id));
            return (
              <ListRow
                key={c.id}
                data-channel={c.name}
                lead={<ChannelGlyph channel={c} />}
                {...glyphRow(c, chat)}
                meta={
                  <Badge variant="accent" aria-label={`${unread} unread`}>
                    {unread}
                  </Badge>
                }
              />
            );
          })}
        </div>
      </div>
    </div>
  );
}

/**
 * `/chat/threads` (CHAT-CONTROLS; Slack's Threads view): every thread the person follows
 * (started, replied in, mentioned in, followed by hand or through its topic). THREAD-READS:
 * threads with new replies first under "Unread" (bold, a dot and "n new"), then "Earlier";
 * "Mark all read" in the bar; each row's bell follows or unfollows on hover. An unfollowed row
 * stays on the page (dimmed, its bell crossed) until the person leaves, so it can be followed
 * again from the same place. A row opens the topic with the thread in the side panel (the
 * `thread` focus key), which marks it read.
 */
export function ThreadsPage() {
  const chat = useChat();
  const threads = useQuery({ ...myThreadsQuery(chat.api, chat.co), enabled: chat.meId !== null });
  const actions = useThreadReadActions(chat);
  // Rows unfollowed here, kept in place after the refetch drops them.
  const [dropped, setDropped] = useState<Map<string, ThreadSummaryView>>(() => new Map());
  // A DM's label comes from its members' current names (its dm_key), not the stored name.
  const channels = useQuery(channelsQuery(chat.api, chat.co));
  const byId = new Map((channels.data?.data ?? []).map((c) => [c.id, c]));
  const served = threads.data?.data ?? [];
  const servedIds = new Set(served.map((t) => t.root.id));
  const all = [
    ...served,
    ...[...dropped.values()]
      .filter((t) => !servedIds.has(t.root.id))
      .map((t) => ({ ...t, following: false })),
  ].sort((a, b) => b.last_reply_at.localeCompare(a.last_reply_at));
  const unreadRows = all.filter((t) => t.unread && t.following);
  const earlier = all.filter((t) => !(t.unread && t.following));
  const rows = all;
  const unread = unreadRows.length;
  const now = new Date();
  const toggleFollow = (t: ThreadSummaryView) => {
    const next = !t.following;
    setDropped((d) => {
      const out = new Map(d);
      if (next) out.delete(t.root.id);
      else out.set(t.root.id, { ...t, unread: false, unread_count: 0 });
      return out;
    });
    actions.setFollowing(t.root.thread_id, t.root.id, next);
  };
  const canWrite = chat.meId !== null && !chat.readOnly;
  return (
    <div data-page="chat-threads" className="flex h-full min-h-0 flex-col">
      <ChatPageBar
        title="Threads"
        actions={
          canWrite && rows.length ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              data-action="threads-mark-all-read"
              disabled={unread === 0 || actions.readingAll}
              onClick={() => actions.markAllRead()}
            >
              <CheckCheckIcon data-icon="inline-start" />
              Mark all read
            </Button>
          ) : null
        }
      >
        {rows.length ? (
          <span className="text-xs text-muted-foreground tabular-nums">
            {unread ? `${unread} unread · ` : ""}
            {rows.length} {rows.length === 1 ? "thread" : "threads"}
          </span>
        ) : null}
      </ChatPageBar>
      <div className="min-h-0 flex-1 overflow-y-auto px-3 py-2 max-md:px-1">
        {chat.meId === null ? (
          <EmptyState
            variant="compact"
            icon={MessagesSquareIcon}
            title="Threads are for members"
            description="Agency staff read chat without a membership, so there is no thread of theirs here."
          />
        ) : null}
        {threads.isError ? <LoadError what="Threads" error={threads.error} /> : null}
        {threads.isPending && chat.meId ? <Loading label="Loading threads" /> : null}
        {threads.isSuccess && !rows.length ? (
          <EmptyState
            variant="compact"
            icon={MessagesSquareIcon}
            title="No threads yet"
            description="Threads you start, reply in or follow collect here."
          />
        ) : null}
        {(
          [
            ["unread", "Unread", unreadRows],
            ["earlier", "Earlier", earlier],
          ] as const
        ).map(([key, label, group]) =>
          group.length ? (
            <section key={key} data-thread-group={key} className="flex flex-col">
              {unreadRows.length ? <GroupHeader label={label} count={group.length} /> : null}
              {group.map((t) => (
                <ThreadRow
                  key={t.root.id}
                  chat={chat}
                  thread={t}
                  channel={byId.get(t.channel_id)}
                  now={now}
                  {...(canWrite ? { onToggleFollow: () => toggleFollow(t) } : {})}
                />
              ))}
            </section>
          ) : null,
        )}
      </div>
    </div>
  );
}

/** One row of the Threads page: dot and "n new" when unread, a bell to follow or unfollow. */
function ThreadRow({
  chat,
  thread: t,
  channel,
  now,
  onToggleFollow,
}: {
  chat: ChatContext;
  thread: ThreadSummaryView;
  channel: SidebarChannel | undefined;
  now: Date;
  onToggleFollow?: () => void;
}) {
  const author = authorOf(t.root, chat.members, chat.meId);
  const people = t.participant_ids.flatMap((id) => {
    if (!id) return [];
    const m = chat.members.get(id);
    return m
      ? [{ name: m.name, kind: m.kind === "de" ? ("agent" as const) : ("person" as const) }]
      : [];
  });
  const unread = t.unread && t.following;
  const excerpt = t.root.body_text.split("\n")[0] || "(no text)";
  return (
    <ListRow
      data-thread-row={t.root.id}
      data-unread={unread ? "true" : undefined}
      data-following={t.following ? "true" : "false"}
      className={t.following ? undefined : "opacity-60"}
      lead={
        unread ? (
          <span aria-label="Unread" className="block size-2 rounded-full bg-primary" role="img" />
        ) : (
          <MessagesSquareIcon aria-hidden="true" className="size-4 text-faint" />
        )
      }
      title={
        <span className={unread ? "font-semibold text-foreground" : undefined}>{excerpt}</span>
      }
      render={
        <Link
          to="/c/$slug/chat/$channelId/$topicId"
          params={{ slug: chat.company.slug, channelId: t.channel_id, topicId: t.topic_id }}
          search={{ ...keepFocus({}), thread: t.root.id } as never}
        />
      }
      chips={
        <>
          <Badge variant="outline">
            {channelLabel(channel ?? { kind: t.channel_kind, name: t.channel_name }, chat)}
            {t.channel_kind !== "dm" && t.channel_kind !== "group_dm" ? ` › ${t.topic_name}` : ""}
          </Badge>
          {unread ? (
            <Badge variant="accent" data-slot="thread-new-count">
              {t.unread_count} new
            </Badge>
          ) : null}
          {t.following ? null : <Badge variant="outline">Not following</Badge>}
        </>
      }
      meta={`${author.name} · ${t.reply_count} ${t.reply_count === 1 ? "reply" : "replies"} · last ${relativeTime(t.last_reply_at, now)}`}
      trailing={people.length ? <AvatarStack size="xs" items={people} /> : undefined}
      actions={
        onToggleFollow ? (
          <Button
            type="button"
            variant="ghost"
            size="icon-xs"
            data-action="thread-follow"
            aria-pressed={t.following}
            aria-label={t.following ? `Unfollow thread “${excerpt}”` : `Follow thread “${excerpt}”`}
            title={t.following ? "Unfollow thread" : "Follow thread"}
            onClick={onToggleFollow}
          >
            {t.following ? <BellOffIcon /> : <BellIcon />}
          </Button>
        ) : undefined
      }
    />
  );
}

/**
 * `/chat/archived` (CHAT-CONTROLS): the archived channels the person may see, newest archive
 * first; a company admin (or the channel's admin) restores one from its row.
 */
export function ArchivedChannelsPage() {
  const chat = useChat();
  const lists = useChatLists(chat);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const restore = useMutation({
    mutationFn: (id: string) => chat.api.client.channels.unarchive({ params: { co: chat.co, id } }),
    onSuccess: async (c) => {
      await queryClient.invalidateQueries({ queryKey: chatKeys.channels(chat.co) });
      queryClient.setQueryData(chatKeys.channel(chat.co, c.id), c);
      toast.add({ title: `Restored ${channelLabel(c, chat)}`, type: "success" });
      void navigate({
        to: "/c/$slug/chat/$channelId",
        params: { slug: chat.company.slug, channelId: c.id },
        search: keepFocus as never,
      });
    },
  });
  const spaces = new Map((lists.spaces.data?.data ?? []).map((s) => [s.id, s.name]));
  const rows = (lists.channels.data?.data ?? [])
    .filter((c) => c.archived_at && !isDm(c) && listedInChat(c))
    .sort((a, b) => (b.archived_at ?? "").localeCompare(a.archived_at ?? ""));
  const manager = chat.meId !== null && !chat.readOnly && isManager(chat.company);
  return (
    <div data-page="chat-archived" className="flex h-full min-h-0 flex-col">
      <ChatPageBar title="Archived channels">
        {rows.length ? (
          <span className="text-xs text-muted-foreground tabular-nums">
            {rows.length} {rows.length === 1 ? "channel" : "channels"}
          </span>
        ) : null}
      </ChatPageBar>
      <div className="min-h-0 flex-1 overflow-y-auto px-3 py-2 max-md:px-1">
        {/* OPERATOR-ONLY-UX: the page says why chat is read-only here, once. */}
        {!chat.readOnly && isOperatorOnly(chat.company) ? (
          <OperatorOnlyNotice slug={chat.company.slug} className="mb-2 max-w-2xl" />
        ) : null}
        {lists.channels.isError ? <LoadError what="Channels" error={lists.channels.error} /> : null}
        {lists.channels.isPending ? <Loading label="Loading channels" /> : null}
        {lists.channels.isSuccess && !rows.length ? (
          <EmptyState
            variant="compact"
            icon={ArchiveRestoreIcon}
            title="Nothing archived"
            description="Archived channels keep their messages and can be restored from here."
          />
        ) : null}
        <div className="flex flex-col">
          {rows.map((c) => (
            <ListRow
              key={c.id}
              data-channel={c.name}
              lead={<ChannelGlyph channel={c} />}
              {...glyphRow(c, chat)}
              chips={<Badge variant="outline">{spaces.get(c.space_id ?? "") ?? "No space"}</Badge>}
              meta={`Archived ${new Date(c.archived_at ?? "").toLocaleDateString()}${c.description ? ` · ${c.description}` : ""}`}
              trailing={
                manager || c.joined ? (
                  <RestoreButton chat={chat} channel={c} onRestore={() => restore.mutate(c.id)} />
                ) : undefined
              }
            />
          ))}
        </div>
      </div>
    </div>
  );
}

/** Restore shows for the channel's managers: company admins at once, channel admins by their row. */
function RestoreButton({
  chat,
  channel,
  onRestore,
}: {
  chat: ChatContext;
  channel: SidebarChannel;
  onRestore: () => void;
}) {
  const members = useQuery({
    ...membersQuery(chat.api, chat.co, channel.id),
    enabled: !isManager(chat.company),
    retry: false,
  });
  const can =
    isManager(chat.company) ||
    (members.data?.data.some((m) => m.membership_id === chat.meId && m.role === "admin") ?? false);
  if (!can) return null;
  return (
    <Button
      size="sm"
      variant="secondary"
      className="relative z-10"
      aria-label={`Restore ${channelLabel(channel, chat)}`}
      onClick={onRestore}
    >
      <ArchiveRestoreIcon data-icon="inline-start" />
      Restore
    </Button>
  );
}

export function SavedMessages() {
  const chat = useChat();
  const saved = useQuery({ ...bookmarksQuery(chat.api, chat.co), enabled: chat.meId !== null });
  const channels = useQuery(channelsQuery(chat.api, chat.co));
  const queryClient = useQueryClient();
  const remove = useMutation({
    mutationFn: (b: { message: { id: string; thread_id: string } }) =>
      chat.api.client.messages.bookmark({
        params: { co: chat.co, id: b.message.thread_id, mid: b.message.id },
        body: { bookmarked: false },
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: chatKeys.bookmarks(chat.co) }),
  });
  const byId = new Map((channels.data?.data ?? []).map((c) => [c.id, c]));
  const rows = saved.data?.data ?? [];
  return (
    <div data-page="chat-saved" className="flex h-full min-h-0 flex-col">
      <ChatPageBar title="Saved messages" />
      <div className="min-h-0 flex-1 overflow-y-auto px-3 py-2 max-md:px-1">
        {/* CHAT-SWITCH-A: Slack's "Later": reminders above the saved messages. */}
        <RemindersSection chat={chat} />
        <ScheduledSection chat={chat} />
        {chat.meId === null ? (
          <EmptyState
            variant="compact"
            icon={BookmarkIcon}
            title="Saving is for members"
            description="Agency staff read chat without a membership, so there is nothing saved here."
          />
        ) : null}
        {saved.isError ? <LoadError what="Saved messages" error={saved.error} /> : null}
        {saved.isPending && chat.meId ? <Loading label="Loading saved messages" /> : null}
        {saved.isSuccess && !rows.length ? (
          <EmptyState
            variant="compact"
            icon={BookmarkIcon}
            title="Nothing saved yet"
            description="Use a message's bookmark button to keep it here."
          />
        ) : null}
        <div className="flex flex-col">
          {rows.map((b) => {
            const c = b.channel_id ? byId.get(b.channel_id) : undefined;
            const author = b.message.author_membership_id
              ? (chat.members.get(b.message.author_membership_id)?.name ?? "Someone")
              : "CompanyOS";
            return (
              <ListRow
                key={b.message.id}
                lead={<BookmarkIcon aria-hidden="true" className="size-4 text-primary-text" />}
                title={b.message.body_text || "(no text)"}
                {...(c && b.topic_id
                  ? {
                      render: (
                        <Link
                          to="/c/$slug/chat/$channelId/$topicId"
                          params={{
                            slug: chat.company.slug,
                            channelId: c.id,
                            topicId: b.topic_id,
                          }}
                          search={keepFocus as never}
                        />
                      ),
                    }
                  : {})}
                chips={c ? <Badge variant="outline">{channelLabel(c, chat)}</Badge> : undefined}
                meta={`${author} · ${new Date(b.message.created_at).toLocaleString()}`}
                trailing={
                  <Button
                    size="sm"
                    variant="ghost"
                    className="relative z-10"
                    aria-label="Remove from saved"
                    onClick={() => remove.mutate(b)}
                  >
                    Remove
                  </Button>
                }
              />
            );
          })}
        </div>
      </div>
    </div>
  );
}
